/**
 * 读态的**响应式外壳**——只做「把 `utils/readState.ts` 的纯判定接到 ref 上」这一件事。
 *
 * 判据本体一律不在这里写：那是 `utils/readState.ts` 的活，它零 import、可被 node 直接断言。
 * 本文件依赖 `vue`，只能在 Vite 里跑，所以任何 `if` 出来的业务判断放这里都等于把判据搬出可验证面。
 *
 * ── 这里**没有**文案、也没有 `showEmpty`（故意的）───────────────────────────────────
 * 空态门与三态措辞都归渲染出口 `components/ReadStateEmpty.vue`。同一格判据放两处，就会两处各漏
 * 一支——那正是 §6.4 那 10 处渲染点的成因。所以本外壳只交一样东西：`state`。
 *
 * `loading` 是**独立的一格**、不进 `ReadState`：它是渲染期的瞬时态，与「读到了什么」正交；
 * 把它塞进状态源会逼出 `idle/loading/ok/empty/forbidden/failed/malformed` 七格互斥，而七格互斥
 * 表达不了「加载中但上一轮已 403」这种真实组合（定版件 §6.1 把我的实现引用为七态，实况是五格，
 * 见 `UI-SCREEN-DIFF-FRONTEND-20260911.md` §6.3——分歧恰好落在 `loading` 上）。
 */
import { ref, shallowRef } from 'vue';
import {
  classifyReadError,
  classifyReadShape,
  readErrorShapeOf,
  stateOfReadShape,
  type ReadState,
} from '../utils/readState';

/**
 * 把一次读取的三种结局（成功／非 2xx／2xx 但形状不合）收敛成同一格 `ReadState`。
 *
 * `loader` 必须返回**已解包的 data**（`unwrap` 之后），且本层不认识业务字段：行到类型的投影
 * 由 `project` 提供（缺省＝原样透传）。
 *
 * 返回被投影后的行；抛错一律**不外抛**——非 2xx 已经落成 `failed`／`forbidden`，外抛只会诱导
 * 调用方写 `catch {}` 把判据吞掉（那正是这 10 处渲染点的原始缺陷）。要拿异常请读 `state`。
 */
export function useReadState<T = unknown>() {
  const state = ref<ReadState>({ kind: 'idle' });
  const loading = ref(false);
  const rows = shallowRef<T[]>([]);

  async function load(loader: () => Promise<unknown>, project?: (rows: unknown[]) => T[]): Promise<T[]> {
    loading.value = true;
    try {
      const data = await loader();
      const verdict = classifyReadShape(data);
      state.value = stateOfReadShape(verdict);
      rows.value = verdict.kind === 'list' ? (project ? project(verdict.rows) : (verdict.rows as T[])) : [];
      return rows.value;
    } catch (error) {
      state.value = classifyReadError(readErrorShapeOf(error));
      rows.value = [];
      return rows.value;
    } finally {
      loading.value = false;
    }
  }

  /** 换条件重查前先复位，免得上一轮的 403 文案挂在新结果上（复位后是 `idle`，也不出空态） */
  function reset(): void {
    state.value = { kind: 'idle' };
    rows.value = [];
  }

  return { state, loading, rows, load, reset };
}
